import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { IComponent } from "./component.interface";
import { PnlComponent } from "./pnl.component";
import { TradePlotLineComponent } from "./trade-plot-line.component";

export class DrawTradeOrdersComponent implements IComponent {
    public id = '';

    public tradePlotLines: Array<TradePlotLineComponent> = [];
    public tradeOrders: Array<TradeOrderDataModel> = [];
    public removeTradeIds: Array<string> = [];
    private base: IChartContainer;
    private isRemovingForSwitchingSymbol = false;
    private canDraw = false;
    public initialize = false;

    constructor(base: IChartContainer) {
        this.base = base;

        if (!this.base.marketingWebChartMode) {
            this.canDraw = true;
        }

        this.subscribeDrawTradeOrders();
        this.subscribeChartEvents();
    }

    public hasAnyTradeOrders(): boolean {
        return this.tradeOrders.length > 0;
    }

    public getOriginalOrderPrice(id: string): number | null {
        return this.originalTradeOrders.find(o => o.id === id)?.price ?? null;
    }

    public remove() {
        this.isRemovingForSwitchingSymbol = true;
        if (this.base.marketingWebChartMode) {
            this.canDraw = false;
        }

        for (let i = 0; i < this.tradePlotLines.length; i++) {
            this.tradePlotLines[i].remove();
            this.tradePlotLines[i] = null;
        }

        this.tradeOrders = [];
        this.originalTradeOrders = [];
        this.allTradeOrders = [];
        this.tradePlotLines = [];
        this.removeTradeIds = [];
        this.initialize = false;

        this.isRemovingForSwitchingSymbol = false;
    }

    public enable() {
        if (this.canDraw) return;

        this.canDraw = true;
        this.updateTrades();
    }

    public disable() {
        this.canDraw = false;
        for (let i = 0; i < this.tradePlotLines.length; i++) {
            this.tradePlotLines[i].remove();
            this.tradePlotLines[i] = null;
        }

        this.tradePlotLines = [];

    }

    public get elementMaximumWidth(): number {
        let width = 0;
        if (this.tradePlotLines?.length) {
            for (let tradeOrderLine of this.tradePlotLines) {
                if (tradeOrderLine.elementWidth > width) {
                    width = tradeOrderLine.elementWidth;
                }
            }
        }

        return width;
    }

    private subscribeChartEvents() {
        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            for (const tradeOrder of this.tradePlotLines) {
                tradeOrder.onExtreamChange();
            }
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            for (const tradeOrder of this.tradePlotLines) {
                tradeOrder.onRedraw();
            }
        });
    }

    private drawTradeOrdersTimer: any;
    private chartStyle: ChartStyleSettingsModel | null = null;
    private originalTradeOrders: Array<TradeOrderDataModel> = [];
    public allTradeOrders: Array<TradeOrderDataModel> = [];
    private subscribeDrawTradeOrders() {
        this.base.chartService?.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            this.chartStyle = chartStyle;
            this.populateTradeOrders();
        });
        this.base.chartService.subscribeDrawTradeOrdersData((tradeOrders: Array<TradeOrderDataModel>) => {
            this.originalTradeOrders = tradeOrders;
            this.populateTradeOrders();
        });
        this.base.chartService.subscribeAllTradeOrdersData((allTradeOrders: Array<TradeOrderDataModel>) => {
            this.allTradeOrders = allTradeOrders;
            this.populateAllTradeOrders();
        });
    }

    private populateAllTradeOrders() {
        const nonChartMode = this.chartStyle?.nonNormalChartMode();
        if (this.isRemovingForSwitchingSymbol || nonChartMode) {
            return;
        }

        for (const tradeOrder of this.tradePlotLines) {
            tradeOrder.updateAllTradeOrders(this.allTradeOrders);
        }
    }

    private previousTradeOrders: Array<TradeOrderDataModel> = [];
    private populateTradeOrders() {
        const nonChartMode = this.chartStyle?.nonNormalChartMode();
        if (this.isRemovingForSwitchingSymbol || nonChartMode) {
            return;
        }

        // Find orders that previously allowed moving but now don't
        const previouslyMovableNowFixed = this.findPreviouslyMovableOrders();

        this.tradeOrders = [];
        for (const t of this.originalTradeOrders) {

            if (this.chartStyle?.chartSettingsPopupModel?.defaultEnableTradeOrderEntry && !!t.ocaGroup) {
                continue;
            }

            if (t.tradeType?.toLowerCase() == 'market' || !t.allowMoving || this.removeTradeIds.includes(t.id)) {
                continue;
            }

            this.tradeOrders.push(t);
        }

        // Update previous state for next comparison
        this.previousTradeOrders = [...this.originalTradeOrders];

        if (previouslyMovableNowFixed?.[0]) {
            this.setPnlLastTradeAccount(previouslyMovableNowFixed[0].accountName);
        }

        this.updateTrades();

        if (!this.initialize) {
            setTimeout(() => {
                this.initialize = true;
            }, 500);
        }
    }

    private findPreviouslyMovableOrders(): Array<TradeOrderDataModel> {
        const result: Array<TradeOrderDataModel> = [];

        for (const currentOrder of this.originalTradeOrders) {
            // Find the same order in previous state
            const previousOrder = this.previousTradeOrders.find(prev => prev.id === currentOrder.id);

            if (previousOrder &&
                previousOrder.allowMoving === true &&
                currentOrder.allowMoving === false) {
                result.push(currentOrder);
            }
        }

        return result;
    }

    private updateTrades() {
        const nonChartMode = this.chartStyle?.nonNormalChartMode();
        if (!this.canDraw || nonChartMode) return;

        if (this.base.marketingWebChartMode) {
            for (const tradeOrder of this.tradeOrders) {
                const id = tradeOrder.getClassName();
                const existPlotLine = this.tradePlotLines.find((x: TradePlotLineComponent) => x.id == id);
                if (existPlotLine && existPlotLine.model && !existPlotLine.moveMode && !existPlotLine.selected) {
                    existPlotLine.model.price = tradeOrder.price;
                    existPlotLine.model.limitPrice = tradeOrder.limitPrice;
                    existPlotLine.model.stopPrice = tradeOrder.stopPrice;

                    if (existPlotLine.model.allowMoving != tradeOrder.allowMoving) {
                        existPlotLine.update(tradeOrder);
                    }
                }
            }
        }

        const anyPlotLineMoving = this.tradePlotLines.some((x: TradePlotLineComponent) => x.isMoving());
        if (anyPlotLineMoving) {
            return;
        }

        const anyHovering = this.tradePlotLines.some((x: TradePlotLineComponent) => x.mouseHover);
        if (anyHovering) {
            return;
        }

        clearTimeout(this.drawTradeOrdersTimer);
        this.drawTradeOrdersTimer = setTimeout(() => {
            this.removeTradePlotLinesAfterUpdate();
            this.createTradePlotLines();
        }, 100);
    }

    private removeTradePlotLinesAfterUpdate() {
        const remainingPlotLines: TradePlotLineComponent[] = [];

        for (let i = 0; i < this.tradePlotLines.length; i++) {
            const plotLine = this.tradePlotLines[i];

            if (plotLine.isPending) {
                const currentOrder = this.tradeOrders.find(o => o.getClassName() === plotLine.id);
                if (currentOrder && currentOrder.price === plotLine.pendingOriginalPrice) {
                    remainingPlotLines.push(plotLine);
                    plotLine.debouncePendingRemove();
                    continue;
                }
            }

            plotLine.remove();
        }

        this.tradePlotLines = remainingPlotLines;
    }

    private createTradePlotLines() {
        for (const tradeOrder of this.tradeOrders) {
            const id = tradeOrder.getClassName();
            if (this.tradePlotLines.find(p => p.id === id)) {
                continue;
            }
            this.tradePlotLines.push(new TradePlotLineComponent(this.base, tradeOrder, this));
        }
    }

    private setPnlLastTradeAccount(account: string) {
        const pnl = this.base.getComponentsByType(PnlComponent)[0] as PnlComponent;
        if (pnl && !pnl.selectedAccount) {
            pnl.lastTradeAccount = account;
            pnl.setSelectPositionByAccount(account);
        }
    }
}